import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// --- Core Imports ---
import { useAuth } from "../../contexts/AuthContext";

// --- Authorization Config ---
import { ESS_AUTH } from "../../config/authorizationConfig";

// --- Page Components ---
import MyLeave from "./MyLeave";
import ApplyLeave from "./ApplyLeave";
import Fallback from "./Fallback";
import MyLogs from "./MyLogs";
import MyTimeSheet from "./MyTimeSheet";
import ListOfHolidays from "./ListOfHolidays";
import Policy from "./Policy";
import Reimbursements from "./Reimbursements";
import EmergencyFunds from "./EmergencyFunds";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import UpgradePlan from "../UpgradePlan";
import Payroll from "./Payroll";

// --- Styles ---
// InnerMenu.scss still provides the `.custom-content` card used by the pages
// that haven't been redesigned yet.
import "../../styles/InnerMenu.scss";
import "./ESS.scss";

// Case / trailing-slash insensitive, like react-router's own matching
// ("/ess/Policy" and "/ess/policy" are the same page).
const pathKey = (path = "") => path.toLowerCase().replace(/\/+$/, "");

// Every route uses the shared content wrapper. Redesigned pages also use the
// `.ess-page` scope for ESS-specific layout rules.
const REDESIGNED_ROUTES = [
  "/ess/my-leave",
  "/ess/apply-leave",
  "/ess/fallback",
  "/ess/my-logs",
  "/ess/my-time-sheet",
  "/ess/reimbursements",
  "/ess/emergency-funds",
  "/ess/payroll",
  "/ess/list-of-holidays",
  "/ess/policy",
];

const ESS = () => {
  const { isLoading, canAccessModuleFeature } = useAuth();
  const location = useLocation();

  // Enhanced authorization check that properly validates feature access
  const hasFeatureAccess = (authConfig) => {
    // If no auth config or no feature specified, it's public access
    if (!authConfig || !authConfig.feature) {
      return true;
    }
    return canAccessModuleFeature(authConfig.feature);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  const currentPath = pathKey(location.pathname);

  const isRedesigned = REDESIGNED_ROUTES.includes(currentPath);

  return (
    <div className="ess-shell">
      <div className={`custom-content${isRedesigned ? " ess-page" : ""}`}>
        <Routes>
          {/* Protected Routes with Upgrade Plan Fallback */}
          <Route
            path="/my-leave"
            element={
              hasFeatureAccess(ESS_AUTH.MY_LEAVE) ? (
                <MyLeave />
              ) : (
                <UpgradePlan
                  featureName="My Leave"
                  description="View your personal leave history and balance"
                />
              )
            }
          />
          <Route
            path="/apply-leave"
            element={
              hasFeatureAccess(ESS_AUTH.APPLY_LEAVE) ? (
                <ApplyLeave />
              ) : (
                <UpgradePlan
                  featureName="Apply Leave"
                  description="Submit new leave applications"
                />
              )
            }
          />
          <Route
            path="/fallback"
            element={
              hasFeatureAccess(ESS_AUTH.FALLBACK) ? (
                <Fallback />
              ) : (
                <UpgradePlan
                  featureName="Leave Fallback"
                  description="Manage leave fallback settings"
                />
              )
            }
          />
          <Route
            path="/my-logs"
            element={
              hasFeatureAccess(ESS_AUTH.MY_LOGS) ? (
                <MyLogs />
              ) : (
                <UpgradePlan
                  featureName="My Attendance Logs"
                  description="View your personal attendance history"
                />
              )
            }
          />
          <Route
            path="/my-time-sheet"
            element={
              hasFeatureAccess(ESS_AUTH.MY_TIME_SHEET) ? (
                <MyTimeSheet />
              ) : (
                <UpgradePlan
                  featureName="My Time Sheet"
                  description="Manage your personal timesheet entries"
                />
              )
            }
          />
          <Route
            path="/reimbursements"
            element={
              hasFeatureAccess(ESS_AUTH.REIMBURSEMENTS) ? (
                <Reimbursements />
              ) : (
                <UpgradePlan
                  featureName="Reimbursements"
                  description="Submit and track expense reimbursements"
                />
              )
            }
          />
          <Route
            path="/emergency-funds"
            element={
              hasFeatureAccess(ESS_AUTH.EMERGENCY_FUNDS) ? (
                <EmergencyFunds />
              ) : (
                <UpgradePlan
                  featureName="Emergency Funds"
                  description="Apply for emergency funds and loans"
                />
              )
            }
          />

          <Route
            path="/payroll"
            element={
              hasFeatureAccess(ESS_AUTH.ESS_LOAN) ? (
                <Payroll />
              ) : (
                <UpgradePlan
                  featureName="Payroll"
                  description="View your payslips, salary breakup, and payroll history"
                />
              )
            }
          />

          {/* Public routes within ESS - No authorization required */}
          <Route path="/list-of-holidays" element={<ListOfHolidays />} />
          <Route path="/Policy" element={<Policy />} />

          {/* Default route should redirect to first accessible page */}
          <Route
            path="/"
            element={
              <Navigate
                to={
                  hasFeatureAccess(ESS_AUTH.MY_LEAVE)
                    ? "/ess/my-leave"
                    : "/ess/list-of-holidays"
                }
                replace
              />
            }
          />
        </Routes>
      </div>
    </div>
  );
};

export default ESS;
